import { LockOutlined } from "@ant-design/icons";
import { DatePicker, Form } from "antd";
import { isMaskedValue } from "common/Utility";
import dayjs from "dayjs";
import _ from "lodash";
import { useEffect, useState } from "react";
import SchemaFieldTitle from "../SchemaFieldTitle";
import Tooltip from "../Tooltip";

const ISO_FORMAT = "YYYY-MM-DDTHH:mm:ss";

const getValidDateValue = (value) => {
    let dateValue = value && dayjs(value, ISO_FORMAT);
    if (dateValue && dateValue.isValid() === false) {
        dateValue = null;
    }
    return dateValue;
};

const DateInput = ({ value, onChange, format, disabled }) => {
    const [dateValue, setDateValue] = useState(getValidDateValue(value));

    useEffect(() => {
        setDateValue(getValidDateValue(value));
    }, [value]);

    const onDateValueChange = (date, dateString) => {
        setDateValue(date);
        onChange(date && date.format(ISO_FORMAT));
    };

    return <DatePicker value={dateValue} onChange={onDateValueChange} format={format} disabled={disabled} allowClear />;
};

/** @type {import('./types').IFormDateField} */
function FormDateField({ fieldErrors, fieldId, name, description, max, min, isRequired, dateFormat, dateSeparator, disabled = false, ...field }) {
    const format = `${_.replace(dateFormat, /\$/g, dateSeparator)}`;
    const fieldValue = Form.useWatch([fieldId]);
    const isMasked = isMaskedValue(fieldValue);
    const isDisabled = disabled || (field.isPII && isMasked);

    return (
        <Form.Item
            key={fieldId}
            name={[fieldId]}
            label={
                <div style={{ display: "flex", alignItems: "center" }}>
                    <SchemaFieldTitle name={name} tags={field.classifiers} orientation="horizontal" tagSummary={false} isPrimaryKey={field.isPrimaryKey} />
                    {field.isPII && (
                        <Tooltip title="Masked data due to PII restriction">
                            <LockOutlined style={{ marginLeft: 8, color: "#1890ff" }} />
                        </Tooltip>
                    )}
                </div>
            }
            tooltip={description}
            validateFirst={true}
            rules={[
                {
                    required: isRequired,
                    message: `Value is required`,
                },
                {
                    validator(_, value) {
                        if (value && value.toString().trim()) {
                            let dateValue = dayjs(value, ISO_FORMAT);
                            if (dateValue.isValid() === false) {
                                return Promise.reject(`Value should be a valid date in format ${format}`);
                            }
                            if (min && dateValue.isBefore(dayjs(min, ISO_FORMAT))) {
                                return Promise.reject(`Value should be greater than or equal to ${dayjs(min, ISO_FORMAT).format(format)}`);
                            }

                            if (max && dateValue.isAfter(dayjs(max, ISO_FORMAT))) {
                                return Promise.reject(`Value should be less than or equal to ${dayjs(max, ISO_FORMAT).format(format)}`);
                            }
                        }

                        return Promise.resolve();
                    },
                },
            ]}
            {...fieldErrors}
        >
            <DateInput format={format} disabled={isDisabled} placeholder={isMasked ? "Masked PII - Use 'Reveal' to edit" : undefined} />
        </Form.Item>
    );
}

export default FormDateField;
